import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiShieldCheck, HiLightningBolt, HiArrowRight, HiStar, HiX } from 'react-icons/hi';
import { Link } from 'react-router-dom';

const StormDashWidget = () => {
  const [isPopupVisible, setIsPopupVisible] = useState(false);

  useEffect(() => {
    const checkLastShown = () => {
      const lastShown = localStorage.getItem('stormDashLastShown');
      const now = new Date().getTime();
      const oneWeek = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
      
      if (!lastShown || (now - parseInt(lastShown)) > oneWeek) {
        setIsPopupVisible(true);
        localStorage.setItem('stormDashLastShown', now.toString());
      }
    };

    // Check after a short delay to avoid immediate popup
    const timer = setTimeout(checkLastShown, 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleClosePopup = () => {
    setIsPopupVisible(false);
  };

  return (
    <>
      {/* Regular Widget */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="w-full mt-6"
      >
        <div className="glass-morphism-strong rounded-2xl p-4 sm:p-6 group cursor-pointer hover:shadow-lg transition-all duration-300">
          <a 
            href="https://falcondev-team.onrender.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="block"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              {/* Left side - Content */}
              <div className="flex-1 text-left">
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mb-3 space-y-2 sm:space-y-0">
                  <motion.div 
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center self-start sm:self-center overflow-hidden"
                  >
                    <img 
                      src="https://raw.githubusercontent.com/kryptik-dev/falcondevteam/master/blueteddyfloat.png" 
                      alt="Storm Dash" 
                      className="w-6 h-6 object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-6 h-6 bg-white rounded-sm flex items-center justify-center text-blue-600 font-bold text-xs" style={{display: 'none'}}>
                      SD
                    </div>
                  </motion.div>
                  <div className="text-left">
                    <span className="font-jetbrains text-blue-400 tracking-wider text-xs uppercase font-bold">
                      GAME SHOWCASE
                    </span>
                    <div className="text-xs text-gray-400 mt-1">Wave Defense Game</div>
                  </div>
                </div>
                
                {/* Play Now Button - Mobile optimized positioning */}
                <motion.div 
                  whileHover={{ scale: 1.1, x: 5 }}
                  className="flex items-center space-x-2 text-blue-400 group-hover:text-blue-300 transition-colors absolute top-4 right-4 sm:top-6 sm:right-6"
                >
                  <span className="text-xs sm:text-sm font-medium hidden sm:inline">Play Now</span>
                  <HiArrowRight className="text-lg group-hover:translate-x-1 transition-transform" />
                </motion.div>
                
                <h3 className="text-lg sm:text-xl font-space-grotesk font-bold text-white mb-2 text-left pr-16 sm:pr-20">
                  STORM DASH
                </h3>
                
                <h4 className="text-base sm:text-lg font-space-grotesk font-semibold text-blue-400 mb-3 text-left">
                  Epic Wave Defense Adventure
                </h4>
                
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mb-3 text-left space-y-1 sm:space-y-0">
                  <span className="text-gray-400 text-xs">Developer:</span>
                  <div className="flex items-center space-x-3">
                    <span className="text-purple-400 text-xs font-medium">FalconDev Team</span>
                  </div>
                </div>
                
                <p className="text-gray-300 text-sm leading-relaxed mb-3 text-left">
                  Master the art of survival in this intense wave defense game. Equip your character with powerful abilities and develop your own unique playstyle to outlast endless waves of enemies. Strategic thinking meets fast-paced action as you build defenses and fight to survive.
                </p>
                
                {/* Game Features - Mobile optimized layout */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-2 sm:space-y-0 text-xs text-left">
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    className="flex items-center space-x-2"
                  >
                    <HiShieldCheck className="text-green-400 flex-shrink-0" />
                    <span className="text-gray-400">Wave Defense</span>
                  </motion.div>
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    className="flex items-center space-x-2"
                  >
                    <HiLightningBolt className="text-yellow-400 flex-shrink-0" />
                    <span className="text-gray-400">Fast Paced</span>
                  </motion.div>
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    className="flex items-center space-x-2"
                  >
                    <HiStar className="text-purple-400 flex-shrink-0" />
                    <span className="text-gray-400">Strategic</span>
                  </motion.div>
                </div>
                
                {/* Beta Notice */}
                <div className="mt-3 text-xs text-amber-400 bg-amber-400/10 rounded-lg px-3 py-2 border border-amber-400/20">
                  <span className="font-medium">⚠️ Early Beta Version:</span> Some features may not work as expected.
                </div>
              </div>
            </div>
          </a>
        </div>
      </motion.div>

      {/* Weekly Popup */}
      <AnimatePresence>
        {isPopupVisible && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative w-full max-w-2xl"
            >
              {/* Close Button */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleClosePopup}
                className="absolute -top-4 -right-4 z-10 w-8 h-8 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white shadow-lg transition-colors"
              >
                <HiX className="w-5 h-5" />
              </motion.button>

              <div className="glass-morphism-strong backdrop-blur-md rounded-2xl p-4 sm:p-6 group cursor-pointer hover:shadow-lg transition-all duration-300">
                <a 
                  href="https://falcondev-team.onrender.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                    {/* Left side - Content */}
                    <div className="flex-1 text-left">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mb-3 space-y-2 sm:space-y-0">
                        <motion.div 
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center self-start sm:self-center overflow-hidden"
                        >
                          <img 
                            src="https://raw.githubusercontent.com/kryptik-dev/falcondevteam/master/blueteddyfloat.png" 
                            alt="Storm Dash" 
                            className="w-6 h-6 object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="w-6 h-6 bg-white rounded-sm flex items-center justify-center text-blue-600 font-bold text-xs" style={{display: 'none'}}>
                            SD
                          </div>
                        </motion.div>
                        <div className="text-left">
                          <span className="font-jetbrains text-blue-400 tracking-wider text-xs uppercase font-bold">
                            GAME SHOWCASE
                          </span>
                          <div className="text-xs text-gray-400 mt-1">Wave Defense Game</div>
                        </div>
                      </div>
                      
                      {/* Play Now Button - Mobile optimized positioning */}
                      <motion.div 
                        whileHover={{ scale: 1.1, x: 5 }}
                        className="flex items-center space-x-2 text-blue-400 group-hover:text-blue-300 transition-colors absolute top-4 right-4 sm:top-6 sm:right-6"
                      >
                        <span className="text-xs sm:text-sm font-medium hidden sm:inline">Play Now</span>
                        <HiArrowRight className="text-lg group-hover:translate-x-1 transition-transform" />
                      </motion.div>
                      
                      <h3 className="text-lg sm:text-xl font-space-grotesk font-bold text-white mb-2 text-left pr-16 sm:pr-20">
                        STORM DASH
                      </h3>
                      
                      <h4 className="text-base sm:text-lg font-space-grotesk font-semibold text-blue-400 mb-3 text-left">
                        Epic Wave Defense Adventure
                      </h4>
                      
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mb-3 text-left space-y-1 sm:space-y-0">
                        <span className="text-gray-400 text-xs">Developer:</span>
                        <div className="flex items-center space-x-3">
                          <span className="text-purple-400 text-xs font-medium">FalconDev Team</span>
                        </div>
                      </div>
                      
                      <p className="text-gray-300 text-sm leading-relaxed mb-3 text-left">
                        Master the art of survival in this intense wave defense game. Equip your character with powerful abilities and develop your own unique playstyle to outlast endless waves of enemies. Strategic thinking meets fast-paced action as you build defenses and fight to survive.
                      </p>
                      
                      {/* Game Features - Mobile optimized layout */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-2 sm:space-y-0 text-xs text-left">
                        <motion.div 
                          whileHover={{ scale: 1.05 }}
                          className="flex items-center space-x-2"
                        >
                          <HiShieldCheck className="text-green-400 flex-shrink-0" />
                          <span className="text-gray-400">Wave Defense</span>
                        </motion.div>
                        <motion.div 
                          whileHover={{ scale: 1.05 }}
                          className="flex items-center space-x-2"
                        >
                          <HiLightningBolt className="text-yellow-400 flex-shrink-0" />
                          <span className="text-gray-400">Fast Paced</span>
                        </motion.div>
                        <motion.div 
                          whileHover={{ scale: 1.05 }}
                          className="flex items-center space-x-2"
                        >
                          <HiStar className="text-purple-400 flex-shrink-0" />
                          <span className="text-gray-400">Strategic</span>
                        </motion.div>
                      </div>
                      
                      {/* Beta Notice */}
                      <div className="mt-3 text-xs text-amber-400 bg-amber-400/10 rounded-lg px-3 py-2 border border-amber-400/20">
                        <span className="font-medium">⚠️ Early Beta Version:</span> Some features may not work as expected.
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default StormDashWidget;
